import { useState, useRef, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { album, formatAlbumSubtitle, type LyricLine, type Song } from "./data/album";
import { InstallHelp, isInstallHelpDismissed, type InstallHelpHandle } from "./InstallHelp";
import { fetchAuthSession, isAddToHomeScreen } from "@/authSessionClient";

type RepeatMode = "off" | "one" | "album";

function lyricsHaveSyncTiming(lyrics: readonly LyricLine[]) {
  return lyrics.some((l) => typeof l.start === "number" && typeof l.end === "number");
}

function getActiveSyncedLyricIndex({ lyrics, time }: { lyrics: readonly LyricLine[]; time: number }) {
  for (let i = 0; i < lyrics.length; i++) {
    const line = lyrics[i];
    if (!line || typeof line.start !== "number" || typeof line.end !== "number") continue;
    if (time >= line.start && time < line.end) return i;
  }
  return -1;
}

function getFocusedSyncedLyricIndex({ lyrics, time }: { lyrics: readonly LyricLine[]; time: number }) {
  const inWindow = getActiveSyncedLyricIndex({ lyrics, time });
  if (inWindow >= 0) return inWindow;
  let lastStarted = -1;
  for (let i = 0; i < lyrics.length; i++) {
    const line = lyrics[i];
    if (!line || typeof line.start !== "number" || typeof line.end !== "number") continue;
    if (time >= line.start) lastStarted = i;
  }
  return lastStarted;
}

function isSyncedLyricLineHighlighted({ line, time }: { line: LyricLine; time: number }) {
  if (typeof line.start !== "number" || typeof line.end !== "number") return false;
  return time >= line.start;
}

function isMobileIOSOrAndroid() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const iOS =
    /iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  return iOS || /android/i.test(ua);
}

async function checkRelatedAppsInstalled() {
  if (typeof navigator === "undefined") return false;
  if (!("getInstalledRelatedApps" in navigator)) return false;
  try {
    const relatedApps = await (
      navigator as Navigator & {
        getInstalledRelatedApps: () => Promise<{ platform?: string }[]>;
      }
    ).getInstalledRelatedApps();

    return relatedApps.length > 0;
  } catch {
    return false;
  }
}

/** Fire-and-forget play analytics for the authenticated session. */
const MINI_DOC_THUMBNAIL_URL = "https://kennedyryon.com/files/69f9f23695377401953048b3";

function recordPlayInteraction({ trackId }: { trackId: string }) {
  void fetch("/api/interactions/play", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ trackId }),
  });
}

export function MusicPlayer() {
  const [currentSong, setCurrentSong] = useState<Song | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [lyricsFullscreenOpen, setLyricsFullscreenOpen] = useState(false);
  const [repeatMode, setRepeatMode] = useState<RepeatMode>("off");
  const [isEvolutionKit, setIsEvolutionKit] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const installHelpRef = useRef<InstallHelpHandle | null>(null);
  const activeLyricLineRef = useRef<HTMLParagraphElement | null>(null);
  const isAddToHomeScreenLaunch = useMemo(() => isAddToHomeScreen(), []);
  const [appInstalled, setAppInstalled] = useState(false);

  const playableAlbum = useMemo(
    () => (isEvolutionKit ? album : album.slice(0, Math.max(0, album.length - 2))),
    [isEvolutionKit],
  );

  useEffect(() => {
    void fetchAuthSession()
      .then((r) => r.json())
      .then((d: { authenticated?: boolean; isEvolutionKit?: boolean }) => {
        if (d.authenticated && d.isEvolutionKit) {
          setIsEvolutionKit(true);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const onAppInstalled = () => setAppInstalled(true);
    window.addEventListener("appinstalled", onAppInstalled);

    let cancelled = false;
    void checkRelatedAppsInstalled().then((installed) => {
      if (!cancelled && installed) setAppInstalled(true);
    });

    return () => {
      cancelled = true;
      window.removeEventListener("appinstalled", onAppInstalled);
    };
  }, []);

  const handlePlay = (song: Song) => {
    if (audioRef.current) {
      if (currentSong?.id === song.id && isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        const isNewTrack = currentSong?.id !== song.id;
        if (audioRef.current.src !== song.audioUrl || !audioRef.current.src) {
          audioRef.current.src = song.audioUrl;
        }
        audioRef.current.play();
        setCurrentSong(song);
        setIsPlaying(true);
        if (isNewTrack) {
          recordPlayInteraction({ trackId: song.trackId });
        }
      }
    }
  };

  const goToNext = () => {
    if (!currentSong) {
      playableAlbum[0] && handlePlay(playableAlbum[0]);
      return;
    }
    const currentIndex = playableAlbum.findIndex((s) => s.id === currentSong.id);
    const nextIndex = currentIndex >= playableAlbum.length - 1 ? 0 : currentIndex + 1;
    const next = playableAlbum[nextIndex];
    next && handlePlay(next);
  };

  const goToPrevious = () => {
    if (!currentSong) {
      playableAlbum[0] && handlePlay(playableAlbum[0]);
      return;
    }
    const currentIndex = playableAlbum.findIndex((s) => s.id === currentSong.id);
    const prevIndex = currentIndex <= 0 ? playableAlbum.length - 1 : currentIndex - 1;
    const prev = playableAlbum[prevIndex];
    prev && handlePlay(prev);
  };

  const handleAudioEnded = () => {
    const audio = audioRef.current;
    if (!currentSong || !audio) return;

    if (repeatMode === "one") {
      audio.currentTime = 0;
      void audio.play();
      recordPlayInteraction({ trackId: currentSong.trackId });
      return;
    }

    const currentIndex = playableAlbum.findIndex((s) => s.id === currentSong.id);

    if (repeatMode === "album") {
      const nextIndex = currentIndex >= playableAlbum.length - 1 ? 0 : currentIndex + 1;
      const next = playableAlbum[nextIndex];
      next && handlePlay(next);
      return;
    }

    if (currentIndex < 0 || currentIndex >= playableAlbum.length - 1) {
      audio.currentTime = 0;
      setCurrentTime(0);
      audio.pause();
      setIsPlaying(false);
      return;
    }
    const next = playableAlbum[currentIndex + 1];
    next && handlePlay(next);
  };

  const toggleRepeatAlbum = () => {
    setRepeatMode((m) => (m === "album" ? "off" : "album"));
  };

  const toggleRepeatOne = () => {
    setRepeatMode((m) => (m === "one" ? "off" : "one"));
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => {
      setCurrentTime(audio.currentTime);
      if ("mediaSession" in navigator && "setPositionState" in navigator.mediaSession) {
        const d = audio.duration;
        if (!isNaN(d) && isFinite(d) && d > 0) {
          try {
            navigator.mediaSession.setPositionState({
              duration: d,
              playbackRate: audio.playbackRate,
              position: audio.currentTime,
            });
          } catch {
            // Ignore if position state cannot be set
          }
        }
      }
    };
    const updateDuration = () => setDuration(audio.duration);

    audio.addEventListener("timeupdate", updateTime);
    audio.addEventListener("loadedmetadata", updateDuration);

    return () => {
      audio.removeEventListener("timeupdate", updateTime);
      audio.removeEventListener("loadedmetadata", updateDuration);
    };
  }, []);

  useEffect(() => {
    if (!("mediaSession" in navigator)) return;

    if (currentSong) {
      const base = `${window.location.origin}/assets`;
      navigator.mediaSession.metadata = new MediaMetadata({
        title: currentSong.title,
        artist: currentSong.artist,
        album: currentSong.album,
        artwork: [
          { src: `${base}/evolve_96.jpeg`, sizes: "96x96", type: "image/jpeg" },
          { src: `${base}/evolve_128.jpeg`, sizes: "128x128", type: "image/jpeg" },
          { src: `${base}/evolve_192.jpeg`, sizes: "192x192", type: "image/jpeg" },
          { src: `${base}/evolve_256.jpeg`, sizes: "256x256", type: "image/jpeg" },
          { src: `${base}/evolve_384.jpeg`, sizes: "384x384", type: "image/jpeg" },
          { src: `${base}/evolve_512.jpeg`, sizes: "512x512", type: "image/jpeg" },
        ],
      });
    } else {
      navigator.mediaSession.metadata = null;
    }
  }, [currentSong]);

  useEffect(() => {
    if (!("mediaSession" in navigator)) return;

    navigator.mediaSession.setActionHandler("play", () => {
      if (currentSong) {
        audioRef.current?.play();
      } else {
        playableAlbum[0] && handlePlay(playableAlbum[0]);
      }
    });
    navigator.mediaSession.setActionHandler("pause", () => {
      audioRef.current?.pause();
    });
    navigator.mediaSession.setActionHandler("previoustrack", goToPrevious);
    navigator.mediaSession.setActionHandler("nexttrack", goToNext);
    navigator.mediaSession.setActionHandler("seekto", (e) => {
      const audio = audioRef.current;
      if (!audio) return;
      const t = (e as { seekTime?: number }).seekTime;
      if (typeof t === "number" && !isNaN(t)) {
        audio.currentTime = Math.max(0, Math.min(audio.duration || t, t));
        setCurrentTime(audio.currentTime);
      }
    });

    return () => {
      navigator.mediaSession.setActionHandler("play", null);
      navigator.mediaSession.setActionHandler("pause", null);
      navigator.mediaSession.setActionHandler("previoustrack", null);
      navigator.mediaSession.setActionHandler("nexttrack", null);
      navigator.mediaSession.setActionHandler("seekto", null);
    };
  }, [currentSong, playableAlbum]);

  useEffect(() => {
    if (!lyricsFullscreenOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setLyricsFullscreenOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lyricsFullscreenOpen]);

  useEffect(() => {
    if (!lyricsFullscreenOpen) return;
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevBodyOverflow = document.body.style.overflow;
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    return () => {
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.body.style.overflow = prevBodyOverflow;
    };
  }, [lyricsFullscreenOpen]);

  useEffect(() => {
    if (lyricsFullscreenOpen && !currentSong?.lyrics?.length) {
      setLyricsFullscreenOpen(false);
    }
  }, [currentSong, lyricsFullscreenOpen]);

  const lyricsViewSynced = Boolean(currentSong?.lyrics && lyricsHaveSyncTiming(currentSong.lyrics));

  const focusedSyncedLyricIndex =
    lyricsFullscreenOpen &&
    currentSong?.lyrics &&
    lyricsViewSynced
      ? getFocusedSyncedLyricIndex({ lyrics: currentSong.lyrics, time: currentTime })
      : -1;

  useEffect(() => {
    if (!lyricsFullscreenOpen || focusedSyncedLyricIndex < 0) return;
    activeLyricLineRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [focusedSyncedLyricIndex, lyricsFullscreenOpen]);

  useEffect(() => {
    if (isAddToHomeScreenLaunch || !isMobileIOSOrAndroid() || isInstallHelpDismissed()) return;

    const openInstallHelp = () => {
      installHelpRef.current?.showDialog(true);
    };

    openInstallHelp();
    const t1 = window.setTimeout(openInstallHelp, 50);
    const t2 = window.setTimeout(openInstallHelp, 250);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [isAddToHomeScreenLaunch]);

  const formatTime = (seconds: number) => {
    if (isNaN(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    e.preventDefault();
    const audio = audioRef.current;
    if (!audio || !duration || isNaN(duration) || !currentSong) return;

    const wasPlaying = !audio.paused;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, x / rect.width));
    const newTime = percentage * duration;

    if (audio.readyState >= 2) {
      audio.currentTime = newTime;
      setCurrentTime(newTime);
    } else {
      audio.addEventListener(
        "loadeddata",
        () => {
          audio.currentTime = newTime;
          setCurrentTime(newTime);
          if (wasPlaying) {
            audio.play().catch(() => {});
          }
        },
        { once: true },
      );
    }
  };

  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      } else {
        if (!currentSong) {
          playableAlbum[0] && handlePlay(playableAlbum[0]);
        } else {
          audio.play();
        }
      }
  };

  return (
    <div className="min-h-screen text-white px-8 pt-[max(2rem,calc(2rem+env(safe-area-inset-top)))] pb-[max(9rem,calc(9rem+env(safe-area-inset-bottom)))]">
      {!isAddToHomeScreenLaunch && <InstallHelp ref={installHelpRef} appInstalled={appInstalled} />}
      <div className="max-w-4xl mx-auto">
        <div className="mb-4">
          <img
            src="/assets/evolve.jpeg"
            alt="Can I Evolve? album art"
            className="w-40 mx-auto mb-4 rounded-lg shadow-xl"
          />
          <h1 className="text-2xl font-bold text-center">Can I Evolve?</h1>
          <p className="text-base text-gray-300 text-center">{formatAlbumSubtitle(playableAlbum)}</p>
        </div>

        <div className="space-y-0 pb-6">
          {isEvolutionKit && (
            <Link
              to="/mini-doc"
              className={
                "flex items-center gap-2.5 px-3 py-2 mb-4 rounded-lg cursor-pointer " +
                "bg-white/[0.06] shadow-md shadow-black/40 " +
                "hover:bg-white/[0.1] transition-colors " +
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 " +
                "focus-visible:ring-offset-2 focus-visible:ring-offset-black"
              }
              aria-label="Mini-Doc: 10 Days of Evolution"
            >
              <img
                src={MINI_DOC_THUMBNAIL_URL}
                alt=""
                className="w-11 h-11 rounded-md object-cover shrink-0"
              />
              <div className="min-w-0 flex-1 leading-tight">
                <div className="text-xs font-medium text-primary/90">Mini-Doc:</div>
                <div className="text-base font-semibold text-white leading-tight mt-px">
                  10 Days of Evolution
                </div>
              </div>
            </Link>
          )}
          {playableAlbum.map((song, index) => {
            const isCurrentSong = currentSong?.id === song.id;

            return (
              <div
                key={song.id}
                className={`flex items-baseline gap-3 py-2 transition-all border-b border-gray-700/50 cursor-pointer ${
                  isCurrentSong ? "text-primary" : "text-white hover:text-gray-300"
                }`}
                onClick={() => handlePlay(song)}
              >
                <span
                  className={`w-7 flex-shrink-0 text-right tabular-nums text-sm ${
                    isCurrentSong ? "text-primary/80" : "text-gray-500"
                  }`}
                >
                  {index + 1}
                </span>
                <div className="min-w-0 flex-1 flex items-baseline justify-between gap-3">
                  <div className="text-lg font-normal min-w-0 truncate">{song.title}</div>
                  <span className="text-sm tabular-nums text-gray-500 shrink-0">
                    {song.duration}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        <audio
          ref={audioRef}
          onEnded={handleAudioEnded}
          onPause={() => setIsPlaying(false)}
          onPlay={() => setIsPlaying(true)}
        />
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-black/95 backdrop-blur-lg border-t border-primary/30 pb-[env(safe-area-inset-bottom)]">
        <div className="max-w-4xl mx-auto px-4 py-4">
          {currentSong && (
            <div className="flex items-center gap-4 mb-3">
              <img
                src="/assets/evolve.jpeg"
                alt=""
                className="w-12 h-12 rounded flex-shrink-0 object-cover"
              />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm truncate">{currentSong.title}</div>
                <div className="text-xs text-gray-400 truncate">{currentSong.artist}</div>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                {currentSong.lyrics && currentSong.lyrics.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setLyricsFullscreenOpen(true)}
                    className={
                      "inline-flex items-center justify-center min-h-11 px-5 rounded-full shrink-0 " +
                      "text-sm font-semibold bg-primary text-primary-foreground whitespace-nowrap " +
                      "shadow-md hover:shadow-lg hover:-translate-y-0.5 hover:brightness-110 active:translate-y-0 active:brightness-95 " +
                      "transition-[transform,box-shadow,filter] duration-200 cursor-pointer " +
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-black"
                    }
                    aria-label="Open lyrics"
                  >
                    Lyrics
                  </button>
                )}
                <div className="text-xs text-gray-400 whitespace-nowrap">
                  {formatTime(currentTime)} / {formatTime(duration)}
                </div>
              </div>
            </div>
          )}
          <div className="flex items-center justify-center gap-2 sm:gap-3 flex-wrap">
            <button
              type="button"
              onClick={toggleRepeatAlbum}
              aria-label="Repeat album"
              aria-pressed={repeatMode === "album"}
              title="Repeat album"
              className={`w-10 h-10 flex items-center justify-center rounded-full transition-colors ${
                repeatMode === "album"
                  ? "bg-primary/25 text-primary ring-1 ring-primary/50"
                  : "bg-white/10 text-gray-400 hover:bg-white/20 hover:text-white"
              }`}
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                viewBox="0 0 24 24"
                aria-hidden
              >
                <polyline points="17 1 21 5 17 9" />
                <path d="M3 11V9a4 4 0 0 1 4-4h14" />
                <polyline points="7 23 3 19 7 15" />
                <path d="M21 13v2a4 4 0 0 1-4 4H3" />
              </svg>
            </button>
            <button
              type="button"
              onClick={goToPrevious}
              className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors"
              aria-label="Previous"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M8.445 14.832A1 1 0 0010 14v-2.798l5.445 3.63A1 1 0 0017 15V5a1 1 0 00-1.555-.832L10 8.798V6a1 1 0 00-1.555-.832l-6 4a1 1 0 000 1.664l6 4z" />
              </svg>
            </button>
            <button
              type="button"
              onClick={togglePlayPause}
              className="w-12 h-12 flex items-center justify-center rounded-full bg-primary text-primary-foreground hover:brightness-95 transition-colors"
              aria-label={isPlaying ? "Pause" : "Play"}
            >
              {isPlaying ? (
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              ) : (
                <svg className="w-6 h-6 ml-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                </svg>
              )}
            </button>
            <button
              type="button"
              onClick={goToNext}
              className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors"
              aria-label="Next"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M4.555 5.168A1 1 0 003 6v8a1 1 0 001.555.832L10 11.202V14a1 1 0 001.555-.832l6-4a1 1 0 000-1.664l-6-4A1 1 0 0010 6v2.798l-5.445-3.63z" />
              </svg>
            </button>
            <button
              type="button"
              onClick={toggleRepeatOne}
              aria-label="Repeat song"
              aria-pressed={repeatMode === "one"}
              title="Repeat song"
              className={`w-10 h-10 relative flex items-center justify-center rounded-full transition-colors ${
                repeatMode === "one"
                  ? "bg-primary/25 text-primary ring-1 ring-primary/50"
                  : "bg-white/10 text-gray-400 hover:bg-white/20 hover:text-white"
              }`}
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                viewBox="0 0 24 24"
                aria-hidden
              >
                <polyline points="17 1 21 5 17 9" />
                <path d="M3 11V9a4 4 0 0 1 4-4h14" />
                <polyline points="7 23 3 19 7 15" />
                <path d="M21 13v2a4 4 0 0 1-4 4H3" />
              </svg>
              <span
                className="absolute bottom-1.5 right-1 text-[9px] font-bold leading-none tabular-nums pointer-events-none"
                aria-hidden
              >
                1
              </span>
            </button>
          </div>
          {currentSong && (
            <div
              className="mt-3 h-2 bg-gray-700 rounded-full relative group cursor-pointer"
              onClick={handleProgressClick}
            >
              <div
                className="h-full bg-primary rounded-full transition-all"
                style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }}
              />
              <div className="absolute top-1/2 left-0 w-full h-0 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="h-full bg-primary/30 rounded-full" />
              </div>
            </div>
          )}
        </div>
      </div>

      {lyricsFullscreenOpen && currentSong?.lyrics && currentSong.lyrics.length > 0 && (
        <div className="fixed inset-0 z-[100] flex flex-col bg-zinc-950 text-white pb-[env(safe-area-inset-bottom)]">
          <div className="flex items-center gap-4 px-4 pt-[max(0.75rem,env(safe-area-inset-top))] pb-3 border-b border-white/10 shrink-0">
            <button
              type="button"
              onClick={() => setLyricsFullscreenOpen(false)}
              className="p-2 -ml-2 rounded-full hover:bg-white/10 transition-colors"
              aria-label="Close lyrics"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <div className="min-w-0 flex-1">
              <div className="font-semibold truncate">{currentSong.title}</div>
              <div className="text-sm text-gray-400 truncate">{currentSong.artist}</div>
            </div>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-6 py-6">
            <div className="max-w-xl mx-auto">
              {currentSong.lyrics.map((line, i) => {
                const isHighlighted = lyricsViewSynced && isSyncedLyricLineHighlighted({ line, time: currentTime });
                const isFocused = i === focusedSyncedLyricIndex;
                return (
                  <p
                    key={i}
                    ref={isFocused ? activeLyricLineRef : undefined}
                    aria-current={isFocused ? "true" : undefined}
                    className={`text-center text-lg leading-relaxed py-1 px-2 transition-colors ${
                      isHighlighted
                        ? "text-white font-semibold drop-shadow-sm"
                        : lyricsViewSynced
                          ? "text-gray-500"
                          : "text-gray-200"
                    }`}
                  >
                    {line.text}
                  </p>
                );
              })}
            </div>
          </div>
          <div className="shrink-0 border-t border-primary/30 bg-black/95 backdrop-blur-lg px-4 pt-4">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-4 mb-3">
                <img
                  src="/assets/evolve.jpeg"
                  alt=""
                  className="w-12 h-12 rounded flex-shrink-0 object-cover"
                />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate">{currentSong.title}</div>
                  <div className="text-xs text-gray-400 truncate">{currentSong.artist}</div>
                </div>
                <div className="text-xs text-gray-400 whitespace-nowrap flex-shrink-0">
                  {formatTime(currentTime)} / {formatTime(duration)}
                </div>
              </div>
              <div className="flex items-center justify-center gap-2 sm:gap-3 flex-wrap">
                <button
                  type="button"
                  onClick={toggleRepeatAlbum}
                  aria-label="Repeat album"
                  aria-pressed={repeatMode === "album"}
                  title="Repeat album"
                  className={`w-10 h-10 flex items-center justify-center rounded-full transition-colors ${
                    repeatMode === "album"
                      ? "bg-primary/25 text-primary ring-1 ring-primary/50"
                      : "bg-white/10 text-gray-400 hover:bg-white/20 hover:text-white"
                  }`}
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    viewBox="0 0 24 24"
                    aria-hidden
                  >
                    <polyline points="17 1 21 5 17 9" />
                    <path d="M3 11V9a4 4 0 0 1 4-4h14" />
                    <polyline points="7 23 3 19 7 15" />
                    <path d="M21 13v2a4 4 0 0 1-4 4H3" />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={goToPrevious}
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                  aria-label="Previous"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M8.445 14.832A1 1 0 0010 14v-2.798l5.445 3.63A1 1 0 0017 15V5a1 1 0 00-1.555-.832L10 8.798V6a1 1 0 00-1.555-.832l-6 4a1 1 0 000 1.664l6 4z" />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={togglePlayPause}
                  className="w-12 h-12 flex items-center justify-center rounded-full bg-primary text-primary-foreground hover:brightness-95 transition-colors"
                  aria-label={isPlaying ? "Pause" : "Play"}
                >
                  {isPlaying ? (
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                  ) : (
                    <svg className="w-6 h-6 ml-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                    </svg>
                  )}
                </button>
                <button
                  type="button"
                  onClick={goToNext}
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                  aria-label="Next"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M4.555 5.168A1 1 0 003 6v8a1 1 0 001.555.832L10 11.202V14a1 1 0 001.555-.832l6-4a1 1 0 000-1.664l-6-4A1 1 0 0010 6v2.798l-5.445-3.63z" />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={toggleRepeatOne}
                  aria-label="Repeat song"
                  aria-pressed={repeatMode === "one"}
                  title="Repeat song"
                  className={`w-10 h-10 relative flex items-center justify-center rounded-full transition-colors ${
                    repeatMode === "one"
                      ? "bg-primary/25 text-primary ring-1 ring-primary/50"
                      : "bg-white/10 text-gray-400 hover:bg-white/20 hover:text-white"
                  }`}
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    viewBox="0 0 24 24"
                    aria-hidden
                  >
                    <polyline points="17 1 21 5 17 9" />
                    <path d="M3 11V9a4 4 0 0 1 4-4h14" />
                    <polyline points="7 23 3 19 7 15" />
                    <path d="M21 13v2a4 4 0 0 1-4 4H3" />
                  </svg>
                  <span
                    className="absolute bottom-1.5 right-1 text-[9px] font-bold leading-none tabular-nums pointer-events-none"
                    aria-hidden
                  >
                    1
                  </span>
                </button>
              </div>
              <div
                className="mt-3 mb-4 h-2 bg-gray-700 rounded-full relative group cursor-pointer"
                onClick={handleProgressClick}
              >
                <div
                  className="h-full bg-primary rounded-full transition-all"
                  style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
